import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { loginUrlFor } from '@/lib/url'
import { DeniedState } from '@/components/states'
import { Spinner } from '@/components/ui/Spinner'
import { resolveLandingRoute } from './landing'
import type { PermissionKey } from '@shared/permissions'

/** Route guards. */

/** Blocks until `/auth/me` has answered, so nothing flashes the wrong state. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, mustChangePassword } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <BootSpinner />

  if (status === 'anonymous') {
    // Carry the current location so the user resumes where they were,.
    return <Navigate to={loginUrlFor(location)} replace />
  }

  // While the forced first-sign-in change is outstanding, this is the.
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  return <>{children}</>
}

/** The login screen must not be reachable while signed in. */
export function RequireAnonymous({ children }: { children: ReactNode }) {
  const { status, permissions, mustChangePassword } = useAuth()

  if (status === 'loading') return <BootSpinner />
  if (status === 'anonymous') return <>{children}</>
  if (mustChangePassword) return <Navigate to="/change-password" replace />

  return <Navigate to={resolveLandingRoute(permissions) ?? '/no-access'} replace />
}

/** Permission gate for a whole screen. */
export function RequirePermission({
  anyOf,
  what,
  children,
}: {
  anyOf: PermissionKey[]
  /** Named in the denial copy: "You do not have access to the audit log". */
  what?: string
  children: ReactNode
}) {
  const { permissions } = useAuth()
  const allowed = anyOf.some((p) => permissions.has(p))

  if (!allowed) {
    return (
      <DeniedState
        what={what ?? 'this page'}
        // Key shown only to someone who could grant it.
        permission={permissions.has('users.permissions.manage') ? anyOf.join(' or ') : undefined}
      />
    )
  }

  return <>{children}</>
}

/** Boot spinner, capped. */
function BootSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner size="lg" label="Loading the panel" />
    </div>
  )
}
